import { stateLabels, type TaxPaymentState } from '../types';
import { statusLabel, statusLabels, type DashboardDetail, type DashboardStatus, type OutsidePaymentMethod, OUTSIDE_PAYMENT_METHODS } from '../dashboardTypes';

/**
 * The words the drawer uses for each status and reason. The status and the reason come from the server; this only turns them into plain language. Nothing here
 * states a fact the server did not send (no amounts, dates, codes or contacts are invented), and nothing promises a penalty amount or an outcome.
 */
export type Tone = 'danger' | 'warning' | 'info' | 'success';

export interface Banner { tone: Tone; message: string; detail?: string }

export interface IssueDetails { title: string; explanation: string; nextAction: string }

const EARLIER: Record<string, string> = {
  PendingPayment: 'It was waiting to be paid when the due date passed.',
  InsufficientFunds: 'It had been returned for insufficient funds.',
  Failed: 'The payment attempt had failed.',
  NeedsReview: 'It was waiting for a review.',
};

export function bannerFor(detail: DashboardDetail): Banner {
  const { row } = detail;
  switch (row.status) {
    case 'Overdue':
      return {
        tone: 'danger',
        message: 'This tax payment is past the agency due date and must be completed outside Escochex. Penalties or interest may apply.',
        ...(row.statusBeforeOverdue ? { detail: `Earlier status: ${statusLabel(row.statusBeforeOverdue)}. ${EARLIER[row.statusBeforeOverdue] ?? ''}`.trim() } : {}),
      };
    case 'InsufficientFunds':
      return {
        tone: 'danger',
        message: 'This payment could not be completed because the bank account did not have enough available funds.',
        detail: row.reason === 'InsufficientFundsKotapayManual'
          ? `Insufficient funds was recorded by hand from a Kotapay report${detail.insufficientFunds ? ` on ${detail.insufficientFunds.dateIdentified}` : ''}.`
          : 'The payment partner reported the return.',
      };
    case 'Failed':
      return { tone: 'danger', message: 'This payment did not complete: the payment file was rejected. Nothing was paid.' };
    case 'NeedsReview':
      return { tone: 'warning', message: reviewMessage(row.reason) };
    case 'PendingPayment':
      return { tone: 'info', message: 'This payment is waiting to be processed. Nothing has been sent yet.' };
    case 'PaymentProcessing':
      return { tone: 'info', message: 'This payment is being processed. It cannot be recorded as paid outside until that is resolved.' };
    case 'Paid':
      return { tone: 'success', message: 'This payment was settled in the simulation. Nothing was sent to any agency.' };
    case 'PaidOutsideEscochex':
      return { tone: 'success', message: 'This payment was completed outside Escochex and manually recorded for tracking and reconciliation.' };
    default:
      return { tone: 'info', message: `Status: ${statusLabel(row.status)}.` };
  }
}

function reviewMessage(reason: string): string {
  switch (reason) {
    case 'ReturnReasonNotMapped':
      return 'The payment partner returned this payment with a reason Escochex does not recognize yet, so a person needs to review it before it can continue.';
    case 'OutsidePaymentConflictsWithPayment':
      return 'An outside payment is recorded, but a payment was also submitted or settled through Escochex. Both cannot be right, so a person needs to review it.';
    case 'RecordsConflict':
      return 'The records for this payment do not agree, so it cannot continue automatically. A person needs to review it.';
    default:
      return 'This payment requires operational review before it can continue.';
  }
}

/** The "Failure Details" or "Review Details" block (S37, S43), for the statuses that have one. Null for the others. */
export function issueDetailsFor(detail: DashboardDetail): IssueDetails | null {
  const { row } = detail;
  const reason = row.reason;
  if (row.status === 'Failed' || (row.status === 'Overdue' && row.statusBeforeOverdue === 'Failed')) {
    return {
      title: 'Payment file rejected',
      explanation: 'The payment file was rejected, so the payment did not go through. No money was taken and nothing reached the agency.',
      nextAction: 'Check the tax settings for this company, then retry if Retry Payment is offered. If the tax was paid another way, record it as an outside payment.',
    };
  }
  if (row.status === 'NeedsReview' || (row.status === 'Overdue' && row.statusBeforeOverdue === 'NeedsReview')) {
    switch (reason) {
      case 'ReturnReasonNotMapped':
        return { title: 'Return reason not recognized', explanation: 'The payment was returned with a code Escochex does not map to a known cause.', nextAction: 'Review the partner response and confirm what happened before anything is retried.' };
      case 'OutsidePaymentConflictsWithPayment':
        return { title: 'Outside payment conflicts with a payment', explanation: 'An outside payment record exists, but the payment was also submitted or settled through Escochex.', nextAction: 'Confirm which payment is real. Do not record or retry another payment until this is resolved.' };
      default:
        return { title: 'Records conflict', explanation: 'The payment records need a person to reconcile them.', nextAction: 'Review the payment history and confirm the current state.' };
    }
  }
  if (row.status === 'InsufficientFunds' || (row.status === 'Overdue' && row.statusBeforeOverdue === 'InsufficientFunds')) {
    return {
      title: 'Insufficient funds',
      explanation: 'The bank account did not have enough available funds when the payment was attempted.',
      nextAction: 'Make sure the funds are available and retry if Retry Payment is offered, or record a payment made outside Escochex.',
    };
  }
  return null;
}

export const methodLabel = (method: string): string => OUTSIDE_PAYMENT_METHODS.find((m) => m.value === (method as OutsidePaymentMethod))?.label ?? method;

const EVENT_LABELS: Record<string, string> = {
  OutsidePaymentRecorded: 'Outside payment recorded',
  OutsidePaymentVoided: 'Outside payment record voided',
  InsufficientFundsRecorded: 'Insufficient funds recorded',
  NoteAdded: 'Internal note added',
  Prepared: 'Payment prepared',
  'request-approval': 'Approval requested',
  approve: 'Approved',
  'submit-simulated': 'Submitted (simulated)',
  cancel: 'Cancelled',
  reissue: 'Issued again',
};

/** A readable event name; an event the app does not know is shown as the server sent it, tidied, never dropped. */
export function eventLabel(eventType: string): string {
  const known = EVENT_LABELS[eventType];
  if (known) return known;
  const spaced = eventType.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : eventType;
}

/** A status or simulation state the timeline mentions, in words. */
export function valueLabel(value: string | null): string | null {
  if (!value) return null;
  return statusLabels[value as DashboardStatus] ?? stateLabels[value as TaxPaymentState] ?? value;
}
